import React, { useMemo } from 'react';
import moment from 'moment';
import { Line } from 'rc-progress';
import { ResponsiveContainer, XAxis, YAxis, Tooltip, Legend, Area, AreaChart } from 'recharts';

import { bigNumberToText, getAuthTypeProtocols } from '../../../utils';
import {
    AUTH_TYPES_TO_SHOW,
    FEATURE_FLAGS_NAMES,
    PROTOCOL_DISPLAY_NAMES,
    PROTOCOLS_GRAPHS_COLORS
} from '@silverfort-nodelibs/common/utils/consts';
import { getAuthTypeUsageFromProtocolUsage } from '../../InvestigationUtils';

import styles from './InvestigationCharts.module.css';
import investigationStyles from '../../Investigation.module.css';
import useFeature from '../../../SystemSettings/hooks/featureFlags/useFeature';
import { SYSTEM_MODES } from '@silverfort-nodelibs/common/utils/system-mode-config';

const GraphCustomLegend = () => {
    const systemMode = useFeature(FEATURE_FLAGS_NAMES.SystemMode);
    const authTypeToHide =
        systemMode !== SYSTEM_MODES.ON_PREM ? [AUTH_TYPES_TO_SHOW.RADIUS, AUTH_TYPES_TO_SHOW.PING_ADAPTER] : [];

    const authTypeToShow = useMemo(
        () =>
            Object.keys(AUTH_TYPES_TO_SHOW).filter(
                (authType) => !authTypeToHide.includes(AUTH_TYPES_TO_SHOW[authType])
            ),
        []
    );

    return (
        <div className={styles.graphCustomLegend}>
            {authTypeToShow.map((protocolName) => {
                if (AUTH_TYPES_TO_SHOW[protocolName].protocols.length > 0) {
                    return AUTH_TYPES_TO_SHOW[protocolName].protocols.map((protocol) => {
                        return (
                            <div
                                key={`legend_${protocol}`}
                                className={styles[protocol.replace(' ', '_').toLowerCase()]}>
                                <p>&#x25A0;</p>
                                <div>{PROTOCOL_DISPLAY_NAMES[protocol]}</div>
                            </div>
                        );
                    });
                } else {
                    return (
                        <div
                            key={`legend_${protocolName}`}
                            className={styles[protocolName.replace(' ', '_').toLowerCase()]}>
                            <p>&#x25A0;</p>
                            <div>{PROTOCOL_DISPLAY_NAMES[protocolName]}</div>
                        </div>
                    );
                }
            })}
        </div>
    );
};

const GraphCustomTooltip = ({ active, payload }) => {
    if (active) {
        if (!payload || payload.length === 0) {
            return null;
        }

        const tooltipRows = [];

        for (const protocolData of payload) {
            const protocolDisplayName = PROTOCOL_DISPLAY_NAMES[protocolData.name.toUpperCase()];

            tooltipRows.push(
                <div key={protocolData.name}>
                    <div className={`${styles.count} ${styles[protocolData.name]}`}>
                        <span>{`${protocolDisplayName} - `}</span>
                        {Number(protocolData.value).toLocaleString()}
                    </div>
                </div>
            );
        }

        return (
            <div className={styles.graphCustomTooltip}>
                <div>{moment.unix(payload[0].payload.timestamp).format('LT L')}</div>
                {tooltipRows}
            </div>
        );
    }

    return null;
};

class InvestigationCharts extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            selectedAuthTypes: new Set(),
            selectedProtocols: new Set(),
            protocolsBreakdown: [],
            protocolsToShow: new Set()
        };

        const locale = navigator.languages ? navigator.languages : navigator.language;
        moment.locale(locale);
    }

    selectAuthType(authTypeName) {
        const newSelectedAuthTypes = this.state.selectedAuthTypes;
        const newProtocolsToShow = this.state.protocolsToShow;
        const newSelectedProtocols = this.state.selectedProtocols;

        if (this.state.selectedAuthTypes.has(authTypeName)) {
            newSelectedAuthTypes.delete(authTypeName);
            if (AUTH_TYPES_TO_SHOW[authTypeName].protocols.length > 0) {
                for (const protocol of AUTH_TYPES_TO_SHOW[authTypeName].protocols) {
                    newProtocolsToShow.delete(protocol);
                    newSelectedProtocols.delete(protocol);
                }
            } else {
                newProtocolsToShow.delete(authTypeName);
                newSelectedProtocols.delete(authTypeName);
            }
        } else {
            newSelectedAuthTypes.add(authTypeName);
            if (AUTH_TYPES_TO_SHOW[authTypeName].protocols.length > 0) {
                for (const protocol of AUTH_TYPES_TO_SHOW[authTypeName].protocols) {
                    newProtocolsToShow.add(protocol);
                    newSelectedProtocols.add(protocol);
                }
            } else {
                newProtocolsToShow.add(authTypeName);
                newSelectedProtocols.add(authTypeName);
            }
        }

        const newProtocolBreakdown = Object.keys(AUTH_TYPES_TO_SHOW)
            .map((authType) => {
                const protocols = getAuthTypeProtocols(authType);
                if (newSelectedAuthTypes.has(authType) && protocols.length > 0) {
                    return protocols;
                } else {
                    return [];
                }
            })
            .flat();

        this.setState({
            selectedAuthTypes: newSelectedAuthTypes,
            protocolsBreakdown: newProtocolBreakdown,
            selectedProtocols: newSelectedProtocols,
            protocolsToShow: new Set(newProtocolsToShow)
        });
    }
    selectProtocol(protocolName) {
        const newSelectedProtocols = this.state.selectedProtocols;
        const newProtocolsToShow = this.state.protocolsToShow;

        if (this.state.selectedProtocols.has(protocolName)) {
            newSelectedProtocols.delete(protocolName);
            newProtocolsToShow.delete(protocolName);
        } else {
            newSelectedProtocols.add(protocolName);
            newProtocolsToShow.add(protocolName);
        }
        this.setState({
            selectedProtocols: newSelectedProtocols,
            protocolsToShow: newProtocolsToShow
        });
    }
    render() {
        const authsByProtocolData = [];
        const protocolBreakdownData = [];

        if (this.props.usageByProtocol) {
            const { usedAuthTypesUsage, foundProtocols } = getAuthTypeUsageFromProtocolUsage(
                this.props.usageByProtocol,
                this.props.isSaas
            );

            for (const authTypeName of foundProtocols) {
                const displayName = AUTH_TYPES_TO_SHOW[authTypeName].label;
                const usageCount = usedAuthTypesUsage[authTypeName];
                const usagePrecentage = this.props.totalAuths ? usageCount / this.props.totalAuths : 0;
                authsByProtocolData.push(
                    <div
                        className={`${styles.protocol} ${styles.hoverBox} ${
                            this.state.selectedAuthTypes.has(authTypeName) ? styles.selected : ''
                        }`}
                        key={`protocol_${authTypeName}`}
                        onClick={() => {
                            this.selectAuthType(authTypeName);
                        }}>
                        <div
                            className={`${styles.title} ${
                                this.state.selectedAuthTypes.has(authTypeName) ? styles.selected : ''
                            }`}
                            title={`${bigNumberToText(usageCount)}`}>
                            <div>{displayName}</div>
                            <span>{`(${(usagePrecentage * 100).toFixed(1)}%)`}</span>
                        </div>
                        <Line
                            strokeColor={PROTOCOLS_GRAPHS_COLORS[authTypeName]}
                            percent={(usagePrecentage * 100).toFixed(0)}
                            trailWidth={3}
                            strokeWidth={3}
                        />
                    </div>
                );
            }

            for (const authTypeName of Object.keys(AUTH_TYPES_TO_SHOW)) {
                if (!foundProtocols.includes(authTypeName)) {
                    const displayName = AUTH_TYPES_TO_SHOW[authTypeName].label;
                    authsByProtocolData.push(
                        <div
                            className={`${styles.protocol} ${styles.hoverBox} 
                                ${this.state.selectedAuthTypes.has(authTypeName) ? styles.selected : ''}`}
                            key={`protocol_${authTypeName}`}
                            onClick={() => {
                                this.selectAuthType(authTypeName);
                            }}>
                            <div
                                className={`${styles.title} ${
                                    this.state.selectedAuthTypes.has(authTypeName) ? styles.selected : ''
                                }`}
                                title={`0`}>
                                <div>{displayName}</div>
                                <span>{`(${bigNumberToText(0)})`}</span>
                            </div>
                            <Line
                                strokeColor={PROTOCOLS_GRAPHS_COLORS[authTypeName]}
                                percent={0}
                                trailWidth={3}
                                strokeWidth={3}
                            />
                        </div>
                    );
                }
            }

            for (const protocolName of this.state.protocolsBreakdown) {
                const displayName = PROTOCOL_DISPLAY_NAMES[protocolName];
                const usageCount = this.props.usageByProtocol[protocolName] || 0;
                const usagePrecentage = this.props.totalAuths ? usageCount / this.props.totalAuths : 0;
                protocolBreakdownData.push(
                    <div
                        className={`${styles.protocol} ${styles.hoverBox} ${
                            this.state.selectedProtocols.has(protocolName) ? styles.selected : ''
                        }`}
                        key={`protocol_${protocolName}`}
                        onClick={() => {
                            this.selectProtocol(protocolName);
                        }}>
                        <div
                            className={`${styles.title} ${
                                this.state.selectedProtocols.has(protocolName) ? styles.selected : ''
                            }`}
                            title={`${bigNumberToText(usageCount)}`}>
                            <div>{displayName}</div>
                            <span>{`(${(usagePrecentage * 100).toFixed(1)}%)`}</span>
                        </div>
                        <Line
                            strokeColor={PROTOCOLS_GRAPHS_COLORS[protocolName]}
                            percent={(usagePrecentage * 100).toFixed(0)}
                            trailWidth={3}
                            strokeWidth={3}
                        />
                    </div>
                );
            }
        }
        return (
            <div className={styles.metrics}>
                <div className={`${styles.authsOverTimeGraph} ${styles.hoverBox}`}>
                    {this.props.activityByProtocol && this.props.activityByProtocol.length > 0 && (
                        <ResponsiveContainer width="95%">
                            <AreaChart
                                data={this.props.activityByProtocol}
                                margin={{ top: 5, right: 0, left: 0, bottom: 5 }}>
                                <XAxis
                                    dataKey="timestamp"
                                    type="number"
                                    allowDataOverflow={true}
                                    tickFormatter={(timeStr) => moment.unix(timeStr).local().format('L HH:00')}
                                    stroke="white"
                                    tickCount={6}
                                    domain={
                                        Number.isInteger(this.props.daysToShow)
                                            ? [
                                                  Date.now() / 1000 - 24 * 60 * 60 * this.props.daysToShow,
                                                  Date.now() / 1000
                                              ]
                                            : [
                                                  this.props.activityByProtocol[0].timestamp,
                                                  this.props.activityByProtocol[
                                                      this.props.activityByProtocol.length - 1
                                                  ].timestamp
                                              ]
                                    }
                                />
                                <YAxis
                                    type="number"
                                    allowDecimals={false}
                                    domain={[0, (dataMax) => Math.floor(dataMax * 1.3)]}
                                    width={65}
                                    stroke="white"
                                    tickFormatter={(authsStr) => bigNumberToText(authsStr)}
                                    tick={{ angle: -90 }}
                                    tickLine={false}
                                />
                                <Tooltip cursor={false} content={<GraphCustomTooltip />} />
                                <Legend content={<GraphCustomLegend />} />
                                {Object.keys(PROTOCOL_DISPLAY_NAMES).map((protocolName) => {
                                    if (
                                        this.state.protocolsToShow.size === 0 ||
                                        this.state.protocolsToShow.has(protocolName)
                                    ) {
                                        return (
                                            <Area
                                                key={`protocol_color_${protocolName}`}
                                                type="natural"
                                                isAnimationActive={true}
                                                animationDuration={300}
                                                stackId="1"
                                                dataKey={protocolName.toLowerCase()}
                                                stroke={'none'}
                                                fill={PROTOCOLS_GRAPHS_COLORS[protocolName]}
                                                dot={true}
                                                activeDot={{
                                                    stroke: 'transparent',
                                                    fill: 'white',
                                                    strokeWidth: 1,
                                                    r: 2
                                                }}
                                            />
                                        );
                                    } else {
                                        return <div key={`protocol_color_${protocolName}`}></div>;
                                    }
                                })}
                            </AreaChart>
                        </ResponsiveContainer>
                    )}
                </div>
                {(!this.props.activityByProtocol || this.props.activityByProtocol.length === 0) && (
                    <div className={investigationStyles.banner}>
                        <span>No Data Found</span>
                    </div>
                )}
            </div>
        );
    }
}

export default InvestigationCharts;
